/**
 * The relation rail's drawing (spec §8.15): two absolutely positioned SVGs in the virtualiser's
 * spacer — one per gutter — so connectors scroll with the content without any scroll listener or DOM
 * measurement. Hovering highlights an edge; clicking scrolls to its far endpoint. A bundle draws its
 * trunk and shared stub once; hovering a branch highlights it with its trunk, hovering the trunk the
 * whole bundle.
 */
import type { TreeID } from "loro-crdt";
import { useState } from "react";
import type { BundleTrunk, EdgePath, GroupBrace, RailLayout, Side } from "./layout";

export interface OverlayEndpoints {
  from: TreeID;
  to: TreeID;
  trackId: string;
}

export function RailOverlay({ layout, height, endpoints, onJump }: { layout: RailLayout; height: number; endpoints: Map<string, OverlayEndpoints>; onJump(path: EdgePath): void }) {
  // an edge id, or a bundle id when the trunk is hovered
  const [hovered, setHovered] = useState<string | null>(null);
  const hoveredBundle = hovered?.startsWith("bundle:") ? hovered : (layout.paths.find((p) => p.id === hovered)?.bundleId ?? null);
  const lit = (p: EdgePath) => hovered === p.id || (hovered !== null && hovered === p.bundleId);
  return (
    <>
      {(["left", "right"] as const).map((side) => {
        const width = side === "left" ? layout.leftGutter : layout.rightGutter;
        if (!width) return null;
        return (
          <svg
            key={side}
            data-testid={`rail-overlay-${side}`}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            style={{ position: "absolute", top: 0, [side]: -width, overflow: "visible", pointerEvents: "none", zIndex: 1 }}
          >
            <Markers paths={layout.paths.filter((p) => p.side === side)} side={side} />
            {layout.paths
              .filter((p) => p.side === side)
              .map((p) => {
                const ends = endpoints.get(p.id);
                const dim = hovered !== null && !lit(p);
                // a branch's arrowheads follow its own ends: the row it reaches, and the shared row only through the stub
                const toTarget = p.bundleId ? layout.trunks.find((t) => t.id === p.bundleId)?.shared === "origin" : true;
                const marker = `rail-arrow-${side}-${p.style.stroke.slice(1)}`;
                return (
                  <g key={p.id} opacity={dim ? p.opacity * 0.3 : p.opacity} data-testid="rail-edge-group" data-opacity={p.opacity}>
                    <path
                      data-testid="rail-edge"
                      data-edge-id={p.id}
                      data-track={ends?.trackId}
                      data-from={ends?.from}
                      data-to={ends?.to}
                      data-y1={p.y1}
                      data-y2={p.y2}
                      d={p.d}
                      fill="none"
                      stroke={p.style.stroke}
                      strokeWidth={lit(p) ? p.style.width + 1 : p.style.width}
                      strokeDasharray={p.dasharray}
                      strokeLinecap={p.style.dash === "dotted" ? "round" : "butt"}
                      markerEnd={p.bundleId ? (p.style.arrow === "both" || (toTarget && p.style.arrow !== "none") ? `url(#${marker}-end)` : undefined) : p.style.arrow !== "none" ? `url(#${marker}-end)` : undefined}
                      markerStart={!p.bundleId && p.style.arrow === "both" ? `url(#${marker}-start)` : undefined}
                    />
                    {/* a wide, invisible stroke makes thin connectors easy to hover and click */}
                    <path
                      d={p.d}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={Math.max(10, p.style.width + 8)}
                      style={{ pointerEvents: "stroke", cursor: "pointer" }}
                      data-testid="rail-edge-hit"
                      onMouseEnter={() => setHovered(p.id)}
                      onMouseLeave={() => setHovered((h) => (h === p.id ? null : h))}
                      onClick={() => onJump(p)}
                    >
                      <title>{p.style.label || ""}</title>
                    </path>
                    {p.label && (
                      <text
                        x={p.label.x}
                        y={p.label.y}
                        fontSize={10}
                        fill={p.style.stroke}
                        textAnchor="middle"
                        transform={`rotate(-90 ${p.label.x} ${p.label.y})`}
                        data-testid="rail-edge-label"
                      >
                        {p.label.text}
                      </text>
                    )}
                  </g>
                );
              })}
            {layout.trunks
              .filter((t) => t.side === side)
              .map((t) => (
                <Trunk
                  key={t.id}
                  trunk={t}
                  dim={hovered !== null && hoveredBundle !== t.id}
                  lit={hoveredBundle === t.id}
                  onHover={(on) => setHovered((h) => (on ? t.id : h === t.id ? null : h))}
                />
              ))}
            {layout.braces
              .filter((b) => b.side === side)
              .map((b) => (
                <Brace key={`${b.id}-${side}`} brace={b} />
              ))}
          </svg>
        );
      })}
    </>
  );
}

/** A bundle's trunk, twice the track's width, its stub to the shared row and its label (spec §8.15). */
function Trunk({ trunk: t, dim, lit, onHover }: { trunk: BundleTrunk; dim: boolean; lit: boolean; onHover(on: boolean): void }) {
  const marker = `rail-arrow-${t.side}-${t.style.stroke.slice(1)}`;
  // the shared row is a target, or an origin with arrows at both ends
  const arrow = t.style.arrow === "both" || (t.shared === "target" && t.style.arrow !== "none");
  const width = lit ? t.width + 1 : t.width;
  return (
    <g opacity={dim ? 0.3 : 1} data-testid="rail-bundle" data-bundle-id={t.id} data-members={t.members.length} data-shared={t.shared}>
      <path d={t.d} fill="none" stroke={t.style.stroke} strokeWidth={width} strokeDasharray={t.dasharray} data-testid="rail-bundle-trunk" data-width={t.width} />
      <path d={t.stub} fill="none" stroke={t.style.stroke} strokeWidth={lit ? t.style.width + 1 : t.style.width} markerEnd={arrow ? `url(#${marker}-end)` : undefined} data-testid="rail-bundle-stub" />
      <path
        d={t.d}
        fill="none"
        stroke="transparent"
        strokeWidth={Math.max(12, t.width + 8)}
        style={{ pointerEvents: "stroke" }}
        data-testid="rail-bundle-hit"
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
      >
        <title>{t.style.label || ""}</title>
      </path>
      {t.label && (
        <text x={t.label.x} y={t.label.y} fontSize={10} fill={t.style.stroke} textAnchor="middle" transform={`rotate(-90 ${t.label.x} ${t.label.y})`} data-testid="rail-edge-label">
          {t.label.text}
        </text>
      )}
    </g>
  );
}

function Brace({ brace }: { brace: GroupBrace }) {
  return (
    <g data-testid="rail-group" data-group-id={brace.id}>
      {brace.d && <path d={brace.d} fill="none" stroke="#a08a4d" strokeWidth={1.5} />}
      <text x={brace.label.x} y={brace.label.y} fontSize={9} fill="#a08a4d" textAnchor="middle" data-testid="rail-group-label">
        {brace.label.text}
      </text>
    </g>
  );
}

/** One arrowhead pair per colour; `auto-start-reverse` points both heads along the path. */
function Markers({ paths, side }: { paths: EdgePath[]; side: Side }) {
  const colours = [...new Set(paths.map((p) => p.style.stroke))];
  return (
    <defs>
      {colours.map((c) => (
        <g key={c}>
          {(["end", "start"] as const).map((which) => (
            <marker
              key={which}
              id={`rail-arrow-${side}-${c.slice(1)}-${which}`}
              viewBox="0 0 10 10"
              refX={9}
              refY={5}
              markerWidth={5}
              markerHeight={5}
              orient="auto-start-reverse"
              markerUnits="strokeWidth"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
            </marker>
          ))}
        </g>
      ))}
    </defs>
  );
}
